import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { useLangStore } from '@app/dashboard/store/lang';

import { ImageUploader as ImageUploaderSection } from '@app/dashboard/components/shared/ImageUploader';

import type { IAdvancedOptionsProps } from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import {
  ALLOWED_FORMATS,
  MAX_DIMENSIONS,
  MAX_LOGO_SIZE,
  SINGLE_PAYMENTS_FIELDS,
} from '@app/dashboard/constants/payment-links/single-payments';
import {
  SINGLE_PAYMENT_LINKS_EVENTS,
  SINGLE_PAYMENT_LINKS_EVENTS_PARAMS,
  SINGLE_PAYMENT_LINKS_EVENTS_VALUES,
} from '@app/dashboard/constants/tracking_events/payment_links/single';

const ImageUploader = ({
  setFields,
}: Omit<IAdvancedOptionsProps, 'fields' | 'fullPageStatus'>) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const [previewImage, setPreviewImage] = useState('');

  const handlePreviewImage = (file: File) => {
    const previewUrl = URL.createObjectURL(file);
    setPreviewImage(previewUrl);
  };

  const onLogoSelectHandler = ({ file }: { file?: File }) => {
    if (!file) return;

    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.PRODUCT_IMAGE_UPDATED, {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.ACTION]: previewImage
        ? SINGLE_PAYMENT_LINKS_EVENTS_VALUES.REPLACED
        : SINGLE_PAYMENT_LINKS_EVENTS_VALUES.ADDED,
    });

    handlePreviewImage(file);

    setFields((prev: any) => {
      return {
        ...prev,
        [SINGLE_PAYMENTS_FIELDS.IMAGE]: {
          value: file,
          errorMsg: '',
          isValid: true,
        },
      };
    });
  };

  const onDeleteHandler = () => {
    setPreviewImage('');
    setFields((prev: any) => {
      const updatedFields = { ...prev };
      delete updatedFields[SINGLE_PAYMENTS_FIELDS.IMAGE];
      return updatedFields;
    });

    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.PRODUCT_IMAGE_UPDATED, {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.ACTION]:
        SINGLE_PAYMENT_LINKS_EVENTS_VALUES.REMOVED,
    });
  };

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  return (
    <ImageUploaderSection
      lang={lang}
      size="xs"
      label={t(
        'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.advancedOptions.labels.image'
      )}
      hint={t(
        'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.advancedOptions.placeholders.image'
      )}
      showReplaceBtn
      imgUrl={previewImage || ''}
      imgName={SINGLE_PAYMENTS_FIELDS.IMAGE}
      allowedFormats={ALLOWED_FORMATS}
      allowedSize={MAX_LOGO_SIZE}
      onFileSelect={onLogoSelectHandler}
      onDeleteHandler={onDeleteHandler}
      maxWidth={MAX_DIMENSIONS.WIDTH}
      maxHeight={MAX_DIMENSIONS.HEIGHT}
    />
  );
};

export default ImageUploader;
